import { Injectable, signal } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { offlineDb, OutboxEntry, OutboxOpType } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

// Provisional (client-generated) numeric ids for entities created offline. Always negative
// so they can never collide with a real server-assigned positive auto-increment id — every
// call site that receives an id can check `id < 0` to know it's not yet synced.
let provisionalCounter = 0;
export function generateProvisionalId(): number {
  provisionalCounter -= 1;
  return Date.now() * -1000 + provisionalCounter; // large, monotonically-decreasing, never 0
}

export function isProvisionalId(id: number | null | undefined): boolean {
  return typeof id === 'number' && id < 0;
}

@Injectable({ providedIn: 'root' })
export class OutboxService {
  /** Items not synced yet — waiting, in flight or refused by the server (OFFLINE_PLAN.md 1.9). Drives the
   *  header's "N not synced" badge; refreshed on every queue change below. */
  readonly unsyncedCount = signal(0);
  /** Split of the above: still waiting to be sent (pending / in flight) vs. refused by the server. */
  readonly waitingCount = signal(0);
  readonly refusedCount = signal(0);
  constructor() {
    this.refreshUnsyncedCount();
  }

  async refreshUnsyncedCount(): Promise<number> {
    if (!isOfflineCapableNative()) return 0;
    try {
      const waiting = await offlineDb.outbox.where('status').anyOf('pending', 'inflight').count();
      const refused = await offlineDb.outbox.where('status').equals('rejected').count();
      this.waitingCount.set(waiting);
      this.refusedCount.set(refused);
      this.unsyncedCount.set(waiting + refused);
      return waiting + refused;
    } catch {
      return this.unsyncedCount();
    }
  }

  async enqueue(opType: OutboxOpType, clientUuid: string, payload: unknown, params?: Record<string, string>): Promise<string> {
    const opId = uuidv4();
    const entry: OutboxEntry = {
      opId,
      opType,
      clientUuid,
      payload,
      status: 'pending',
      attempts: 0,
      createdAt: Date.now(),
      ...(params && Object.keys(params).length ? { params } : {}),
    };
    await offlineDb.outbox.add(entry);
    this.refreshUnsyncedCount();
    return opId;
  }

  async getByClientUuid(clientUuid: string): Promise<OutboxEntry[]> {
    return offlineDb.outbox.where('clientUuid').equals(clientUuid).sortBy('seq');
  }

  async getPending(): Promise<OutboxEntry[]> {
    return offlineDb.outbox.where('status').equals('pending').sortBy('seq');
  }

  async getAll(): Promise<OutboxEntry[]> {
    return offlineDb.outbox.orderBy('seq').toArray();
  }

  async getRejected(): Promise<OutboxEntry[]> {
    return offlineDb.outbox.where('status').equals('rejected').sortBy('seq');
  }

  /** Work that still has somewhere to go — 'pending' (queued) plus 'inflight' (mid-request).
   *  Deliberately EXCLUDES 'rejected': those are terminal, are never retried, and only the user
   *  can clear them from the Sync Issues panel. Any gate built on this count must not treat a
   *  rejected entry as "still syncing", or it would stay blocked forever. Used by the company
   *  switcher to refuse a tenant change while one tenant's writes are still queued (switching
   *  would either misattribute them to the new tenant or force us to discard real work). */
  async countUnsynced(): Promise<number> {
    if (!isOfflineCapableNative()) return 0;
    return offlineDb.outbox.where('status').anyOf('pending', 'inflight').count();
  }

  // User-initiated removal from the Sync Issues panel — the only place a rejected (terminal,
  // never-retried) entry should ever be deleted from. Never call this on a pending/inflight
  // entry; that would silently drop real unsynced work.
  async discard(seq: number): Promise<void> {
    await offlineDb.outbox.delete(seq);
    this.refreshUnsyncedCount();
  }

  // User-initiated cancellation of an ENTIRE not-yet-synced offline-created record (e.g.
  // deleting a Transport Unit created offline before its CREATE_TU op has replayed) — unlike
  // discard() above, this deliberately removes pending/inflight entries too, because the
  // record they'd create/modify is itself being deleted. Removes every op for this clientUuid
  // regardless of status (CREATE_TU + any dependent UPDATE_GRID_STYLE/SAVE_TALLY_DETAILS etc.),
  // so nothing gets replayed for a record the user explicitly cancelled.
  async cancelByClientUuid(clientUuid: string): Promise<void> {
    const ops = await this.getByClientUuid(clientUuid);
    await Promise.all(ops.filter(op => op.seq !== undefined).map(op => offlineDb.outbox.delete(op.seq!)));
    this.refreshUnsyncedCount();
  }

  // Synced = done: removed from the phone straight away, with its payload (and any photo/document file
  // inside it) — OFFLINE_PLAN.md 1.10. Nothing reads a 'synced' entry back.
  async markSynced(seq: number): Promise<void> {
    await offlineDb.outbox.delete(seq);
    this.refreshUnsyncedCount();
  }

  /** One-time clean-up for phones that still hold entries kept as 'synced' by earlier app versions. */
  async purgeSynced(): Promise<void> {
    if (!isOfflineCapableNative()) return;
    await offlineDb.outbox.where('status').equals('synced').delete();
  }

  // 4xx/validation failure — not retryable as-is. Kept locally (not deleted) so the payload
  // stays inspectable/recoverable; only a future "discard" action in a sync-issues UI should
  // remove it.
  async markRejected(seq: number, error: string, errorCode?: string, errorMeta?: { existingId?: number; existingEncodedId?: string }): Promise<void> {
    await offlineDb.outbox.update(seq, { status: 'rejected', lastError: error, errorCode, errorMeta });
    this.refreshUnsyncedCount();
  }

  // 5xx/network failure — stays pending for the next sync run to retry.
  // Wait before the next try after a SERVER failure (5xx / timeout / 429), growing with each try
  // (OFFLINE_PLAN.md Phase 2, 2026-09-29) — was "retry on every screen change". After MAX_ATTEMPTS
  // the entry moves to Sync Issues (errorCode RETRY_LIMIT) with a "Try again" button; nothing is lost.
  private static readonly RETRY_WAITS_MS = [5_000, 30_000, 120_000, 300_000, 600_000, 900_000, 1_800_000, 3_600_000, 3_600_000];
  static readonly MAX_ATTEMPTS = 10;

  async markFailedAttempt(seq: number, attempts: number, error: string): Promise<void> {
    if (attempts >= OutboxService.MAX_ATTEMPTS) {
      await offlineDb.outbox.update(seq, {
        status: 'rejected',
        attempts,
        errorCode: 'RETRY_LIMIT',
        lastError: `Tried ${attempts} times — the server kept failing. Last error: ${error}`,
        nextAttemptAt: undefined,
      });
      this.refreshUnsyncedCount();
      return;
    }
    const waits = OutboxService.RETRY_WAITS_MS;
    const wait = waits[Math.min(Math.max(attempts, 1), waits.length) - 1];
    await offlineDb.outbox.update(seq, { status: 'pending', attempts, lastError: error, nextAttemptAt: Date.now() + wait });
  }

  /** Sync Issues "Try again" on an entry that hit the retry limit — back in the queue, counting from zero. */
  async resetForRetry(seq: number): Promise<void> {
    await offlineDb.outbox.update(seq, {
      status: 'pending', attempts: 0, nextAttemptAt: undefined, errorCode: undefined, errorMeta: undefined,
    });
    this.refreshUnsyncedCount();
  }

  /** "Keep mine" on a row saved offline that someone else changed meanwhile (ROW_CONFLICT, 2026-09-29):
   *  back into the queue marked force=1, so the replay saves it without the "changed by someone else"
   *  check — a deliberate overwrite the user chose. */
  async resetForForcedRetry(seq: number): Promise<void> {
    const entry = await offlineDb.outbox.get(seq);
    await offlineDb.outbox.update(seq, {
      status: 'pending', attempts: 0, nextAttemptAt: undefined, errorCode: undefined, errorMeta: undefined,
      params: { ...(entry?.params ?? {}), force: '1' },
    });
    this.refreshUnsyncedCount();
  }
}
